import { DIALOGUE_MARKS, type DialogueMark } from "odyssey-shared";

/**
 * A dialogue choice's marks as the page keeps them (ADR-136 amendment of 2026-10-28, decision 7): the marks in the
 * order the frame sent them, a check's chance as a whole percent, the background a background choice is open to and a
 * locked choice's need. The page never works out a chance; it shows the server's or none.
 */
export interface ChoiceMarks {
  readonly marks: readonly DialogueMark[];
  readonly percent?: number;
  readonly background?: string;
  readonly need?: string;
}

/**
 * A choice with no marks, as a server before the marks sends every choice.
 */
export const NO_MARKS: ChoiceMarks = Object.freeze({ marks: Object.freeze([]) });

/**
 * Reads a validated `dialogue` choice's marks. The schema has held each mark number to `DIALOGUE_MARKS`; a choice with
 * only its line and text has none.
 *
 * @param choice The choice as parsed.
 * @returns Its marks, with each fact present only when the frame gave it.
 */
export function choiceMarksOf(choice: Readonly<Record<string, unknown>>): ChoiceMarks {
  const numbers = choice["marks"];
  if (!Array.isArray(numbers)) {
    return NO_MARKS;
  }
  const chance = choice["chance"];
  const background = choice["background"];
  const need = choice["need"];
  return {
    marks: numbers.map((number: number) => DIALOGUE_MARKS[number] as DialogueMark),
    ...(typeof chance === "number" ? { percent: chance } : {}),
    ...(typeof background === "string" ? { background } : {}),
    ...(typeof need === "string" ? { need } : {}),
  };
}
